import { useState, useEffect } from "react";
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX, X, Ban, RefreshCw
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { API, getAuthHeaders } from "./helpers";

export function GovernanceIndicator({ dossierId, tenantId, onBlockedTransitions }) {
  const [governanceStatus, setGovernanceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (dossierId) loadGovernanceStatus();
  }, [dossierId]);

  const loadGovernanceStatus = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/governance-status`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setGovernanceStatus(data);
        if (onBlockedTransitions) onBlockedTransitions(data.blocked_transitions || []);
      }
    } catch (err) { console.error("Erreur chargement gouvernance:", err); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--oh-bg-elevated)] rounded-lg">
        <RefreshCw className="w-4 h-4 text-[var(--oh-text-muted)] animate-spin" />
        <span className="text-xs text-[var(--oh-text-muted)]">Vérification...</span>
      </div>
    );
  }

  if (!governanceStatus) return null;

  const { global_status, blocked_transitions, rules_evaluated } = governanceStatus;
  const rulesCount = rules_evaluated?.length || 0;

  // P0-F (front-only) — Libellé + couleur DÉRIVÉS ici depuis global_status +
  // rules_evaluated. Le contrat API (global_status / global_status_label) n'est
  // PAS touché ; le moteur de transitions n'est pas modifié.
  // RÈGLE : le vert (succès) n'apparaît QUE si un contrôle a réellement été
  // exécuté avec succès. Un PASS par ABSENCE de règles = NEUTRE (plus de vert
  // trompeur). Le vocabulaire « conformité » (réservé au moteur documentaire
  // ANAH) ne figure plus dans ce composant : ici on parle de progression.
  let config;
  if (global_status === "BLOQUE") {
    config = { icon: ShieldX, label: "Transition bloquée",
      bgColor: "bg-[var(--oh-error-bg)]", borderColor: "border-[var(--oh-error-border)]",
      textColor: "text-[var(--oh-error-text)]", iconColor: "text-[var(--oh-error-text)]" };
  } else if (rulesCount === 0) {
    // Aucune règle applicable → NEUTRE (pas de validation par défaut).
    config = { icon: Shield, label: "Aucun blocage",
      bgColor: "bg-[var(--oh-bg-elevated)]", borderColor: "border-[var(--oh-border)]",
      textColor: "text-[var(--oh-text-secondary)]", iconColor: "text-[var(--oh-text-muted)]" };
  } else if (global_status === "AVERTISSEMENT") {
    // Règles exécutées avec avertissement(s) non bloquant(s) → info, jamais vert.
    config = { icon: ShieldAlert, label: "Avertissement",
      bgColor: "bg-[var(--oh-info-bg)]", borderColor: "border-[var(--oh-info-border)]",
      textColor: "text-[var(--oh-info-text)]", iconColor: "text-[var(--oh-info-text)]" };
  } else {
    // Règles réellement évaluées et validées → vert (succès prouvé).
    config = { icon: ShieldCheck, label: "Transition validée",
      bgColor: "bg-[var(--oh-success-bg)]", borderColor: "border-[var(--oh-success-border)]",
      textColor: "text-[var(--oh-success-text)]", iconColor: "text-[var(--oh-success-text)]" };
  }
  const StatusIcon = config.icon;

  return (
    <div className="relative" data-testid="governance-indicator">
      <button onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${config.bgColor} ${config.borderColor} hover:opacity-80`}>
        <StatusIcon className={`w-4 h-4 ${config.iconColor}`} />
        <span className={`text-sm font-medium ${config.textColor}`}>{config.label}</span>
        {blocked_transitions?.length > 0 && (
          <Badge className="ml-1 bg-red-500 text-[var(--oh-accent-contrast)] text-xs px-1.5">{blocked_transitions.length}</Badge>
        )}
      </button>
      {expanded && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg shadow-xl z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[var(--oh-text)] font-medium flex items-center gap-2">
                <Shield className="w-4 h-4" /> Règles de progression
              </h4>
              <button onClick={() => setExpanded(false)} className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"><X className="w-4 h-4" /></button>
            </div>
            {blocked_transitions?.length > 0 && (
              <div className="mb-4">
                <p className="text-[var(--oh-error-text)] text-xs font-medium mb-2 uppercase tracking-wide">Transitions bloquées</p>
                <div className="space-y-2">
                  {blocked_transitions.map((block, idx) => (
                    <div key={idx} className="p-2 bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)] rounded-lg">
                      <div className="flex items-center gap-2 text-sm">
                        <Ban className="w-3 h-3 text-[var(--oh-error-text)]" />
                        <span className="text-[var(--oh-text-muted)]">{block.from_statut?.replace(/_/g, " ")} → {block.to_statut?.replace(/_/g, " ")}</span>
                      </div>
                      <p className="text-[var(--oh-error-text)] text-xs mt-1">{block.message}</p>
                      <Badge className="mt-1 bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)] text-xs">{block.rule_code}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {rules_evaluated?.length > 0 && (
              <div>
                <p className="text-[var(--oh-text-muted)] text-xs font-medium mb-2 uppercase tracking-wide">Règles évaluées</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(() => {
                    // Dédoublonner par rule_code : verdict final = FAIL si au moins 1 FAIL
                    const ruleMap = {};
                    rules_evaluated.forEach(rule => {
                      const code = rule.rule_code;
                      if (!ruleMap[code]) {
                        ruleMap[code] = { ...rule, count: 1 };
                      } else {
                        ruleMap[code].count++;
                        // FAIL overrides PASS
                        if (rule.status === "FAIL") ruleMap[code].status = "FAIL";
                      }
                    });
                    return Object.values(ruleMap).map((rule, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs p-1.5 bg-[var(--oh-bg-elevated)] rounded">
                        <span className="text-[var(--oh-text-muted)]">{rule.rule_code}</span>
                        <Badge className={rule.status === "PASS" ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : "bg-[var(--oh-error-bg)] text-[var(--oh-error-text)]"}>
                          {rule.status}
                        </Badge>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
            {blocked_transitions?.length === 0 && rules_evaluated?.length === 0 && (
              <div className="text-center py-4">
                {/* P0-F : icône NEUTRE (pas de vert) — aucune règle exécutée = pas de validation. */}
                <Shield className="w-8 h-8 text-[var(--oh-text-muted)] mx-auto mb-2" />
                <p className="text-[var(--oh-text-muted)] text-sm">Aucune règle applicable</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
